import { BorderRadius, Color, Spacing } from '@persona-id/design-tokens';
import { ComponentPropsWithoutRef, PropsWithChildren } from 'react';
import {
  Header,
  ListBox as ListBoxReactAria,
  ListBoxItem as ListBoxItemAria,
  Section,
} from 'react-aria-components';
import styled, { css } from 'styled-components';

import Button, { ButtonProps } from 'design-system/components/Button';
import { ListItem, MenuHeading } from 'design-system/components/Menu';
import Stack from 'design-system/components/Stack';
import { isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import menuItemMixins from 'design-system/mixins/menuItem';
import { paragraphMixins } from 'design-system/mixins/text';

interface ListBoxProps<T extends object>
  extends Omit<
    ComponentPropsWithoutRef<typeof ListBoxReactAria<T>>,
    'slot' | 'autoFocus' | 'orientation' | 'shouldFocusWrap' | 'dragAndDropHooks' | 'layout'
  > {
  /**
   * Called when "Add" button is clicked. Only renders "Add" button if this prop is present.
   */
  onAdd?: ButtonProps['onClick'];

  /**
   * Called when "Delete" button is clicked. Only renders "Delete" button if this prop is present.
   */
  onDelete?: ButtonProps['onClick'];

  /**
   * Whether or not delete button should be disabled.
   */
  deleteDisabled?: boolean;

  /**
   * Whether or not the Add/Delete buttons should be shown.
   */
  editable?: boolean;

  /**
   * Text to display if no options are available.
   */
  emptyText?: string;

  /**
   * Whether or not to expand the box to the full width of the container.
   */
  fullWidth?: boolean;
}

const StyledListBox = styled(ListBoxReactAria)<{ fullWidth?: boolean }>`
  ${paragraphMixins.md};

  box-sizing: border-box;
  background: ${Color.BackgroundPrimary};
  border-radius: ${BorderRadius.Lg};
  border: 1px solid ${Color.BorderDivider};
  padding: ${Spacing[1]};

  ${({ fullWidth }) =>
    !fullWidth &&
    css`
      width: 204px;
    `}
`;

const ListBoxEmptyState = styled.button`
  ${paragraphMixins.md};

  background-color: transparent;
  outline: none;
  border: none;
`;

const ListBoxEmptyStateButton = styled(ListBoxEmptyState)`
  &:hover {
    cursor: pointer;
  }
`;

const ListBox = <T extends object>({
  children,
  onAdd,
  onDelete,
  emptyText,
  deleteDisabled,
  selectedKeys,
  editable = true,
  ...props
}: PropsWithChildren<ListBoxProps<T>>) => {
  return (
    <Stack spacing={Spacing[1]}>
      <StyledListBox
        renderEmptyState={() =>
          onAdd ? (
            <ListBoxEmptyStateButton as='button' onClick={onAdd}>
              {emptyText || 'Click to add an option'}
            </ListBoxEmptyStateButton>
          ) : (
            <ListBoxEmptyState>{emptyText || 'No options.'}</ListBoxEmptyState>
          )
        }
        selectedKeys={selectedKeys}
        {...props}
      >
        {children}
      </StyledListBox>
      {editable && (onAdd || onDelete) && (
        <Stack direction='horizontal' spacing={Spacing[1]}>
          {onAdd && (
            <Button size='sm' variant='basic' width='auto' onClick={onAdd}>
              Add
            </Button>
          )}
          {onDelete && (
            <Button
              size='sm'
              variant='basic'
              width='auto'
              onClick={onDelete}
              disabled={deleteDisabled || selectedKeys == null}
            >
              Delete
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const StyledItem = styled(ListItem)`
  padding: ${Spacing[1]} ${Spacing[2]};
  overflow: hidden;
  white-space: nowrap;

  &${isNotDisabled}${isHovered}:not([data-selected]),
  &${isNotDisabled}${isFocused}:not([data-selected]),
  &[data-hovered]:not([data-selected]) {
    cursor: pointer;
    color: ${({ theme }) => theme.color.text.primary};
    background-color: ${Color.CoreSlate50};
  }

  &[data-selected] {
    ${menuItemMixins.hover};
    outline: none;
    color: ${({ theme }) => theme.color.text.brand};
    background-color: ${({ theme }) => theme.color.background.highlight};
  }

  .react-aria-Section:not(:first-child) {
    margin-top: ${Spacing[1]};
  }
`;

const ListBoxItem = (props: ComponentPropsWithoutRef<typeof ListBoxItemAria>) => {
  return <StyledItem as={ListBoxItemAria} {...props} />;
};

const StyledMenuHeading = styled(MenuHeading)`
  padding: ${Spacing[1]} ${Spacing[2]};
`;

const ListBoxHeader = (props: ComponentPropsWithoutRef<typeof Header>) => {
  return <StyledMenuHeading as={Header} {...props} />;
};

ListBox.Item = ListBoxItem;

ListBox.Section = Section;

ListBox.Header = ListBoxHeader;

export default ListBox;
